import { useState } from "react";
import useApi from "./useApi";
import usePage from "./usePage";

const useThirdPartyInvoice = () => {
  const { getData, postData } = useApi();
  const { showMessage } = usePage();

  const [invoices, setInvoices] = useState([]);
  const [currentInvoice, setCurrentInvoice] = useState(null);
  const [loading, setLoading] = useState(false);

  const getInvoices = async (filter = null) => {
    setLoading(true);
    try {
      const results = await getData({
        entityName: "ThirdPartyInvoice",
        filter: filter || null,
      });

      if (results.isSuccessful) {
        // Convert indexed response to array
        const invoiceArray = results.data ? Object.values(results.data) : [];
        setInvoices(invoiceArray);
      } else {
        setInvoices([]);
        showMessage(results.message || "Error fetching invoices", "error");
      }
    } catch (err) {
      console.error("Error fetching invoices:", err);
      setInvoices([]);
      showMessage("Error fetching invoices", "error");
    } finally {
      setLoading(false);
    }
  };

  const getInvoiceByClientProject = async (clientId, projectId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "getByClientProject",
        data: { clientId, projectId },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data || null);
        return results.data;
      } else {
        setCurrentInvoice(null);
        showMessage(results.message || "Error fetching invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error fetching invoice:", err);
      setCurrentInvoice(null);
      showMessage("Error fetching invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const createInvoice = async (invoiceData) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "add",
        data: invoiceData,
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Invoice created successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error creating invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error creating invoice:", err);
      showMessage("Error creating invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateInvoice = async (invoiceId, invoiceData) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "update",
        data: { id: invoiceId, ...invoiceData },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Invoice updated successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error updating invoice", "error");
        return null;
      }
    } catch (err) {
      console.error("Error updating invoice:", err);
      showMessage("Error updating invoice", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteInvoice = async (invoiceId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "delete",
        data: { id: invoiceId },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(null);
        showMessage("Invoice deleted successfully", "success");
        return true;
      } else {
        showMessage(results.message || "Error deleting invoice", "error");
        return false;
      }
    } catch (err) {
      console.error("Error deleting invoice:", err);
      showMessage("Error deleting invoice", "error");
      return false;
    } finally {
      setLoading(false);
    }
  };

  const addLine = async (invoiceId, line) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "addLine",
        data: { invoiceId, line },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Line added successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error adding line", "error");
        return null;
      }
    } catch (err) {
      console.error("Error adding line:", err);
      showMessage("Error adding line", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const updateLine = async (invoiceId, lineId, line) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "updateLine",
        data: { invoiceId, lineId, line },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Line updated successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error updating line", "error");
        return null;
      }
    } catch (err) {
      console.error("Error updating line:", err);
      showMessage("Error updating line", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  const deleteLine = async (invoiceId, lineId) => {
    setLoading(true);
    try {
      const results = await postData({
        entityName: "ThirdPartyInvoice",
        action: "deleteLine",
        data: { invoiceId, lineId },
      });

      if (results.isSuccessful) {
        setCurrentInvoice(results.data);
        showMessage("Line deleted successfully", "success");
        return results.data;
      } else {
        showMessage(results.message || "Error deleting line", "error");
        return null;
      }
    } catch (err) {
      console.error("Error deleting line:", err);
      showMessage("Error deleting line", "error");
      return null;
    } finally {
      setLoading(false);
    }
  };

  return {
    invoices,
    currentInvoice,
    loading,
    getInvoices,
    getInvoiceByClientProject,
    createInvoice,
    updateInvoice,
    deleteInvoice,
    addLine,
    updateLine,
    deleteLine,
  };
};

export default useThirdPartyInvoice;
